import CloseIcon from "@/assets/img/modal/close_icon.svg"
import LockIcon from "@/assets/img/modal/lock_icon.svg"
import ModalBg from "@/assets/img/modal/modal_bg.webp"
import { useChat } from "@/features/chat"
import { usePlans } from "@/features/products"
import {
  getReportById,
  useReportPurchase,
  type ReportId,
} from "@/features/today"

import type { AppModalComponentProps } from "../config/modal-registry"

export interface ReportPromoCopyV1 {
  reportId: ReportId
  planId: string
  title: string
  description: string
}

interface ReportPromoModalV1Props extends AppModalComponentProps {
  copy: ReportPromoCopyV1
}

/** Fallback when the plans API has no price yet. */
const DEFAULT_PRICE = 9.99

/**
 * Shared unlock sheet for purchasable reports v1 (Figma Popovers_Reports_*_1).
 * Green unlock bar + starry body + huge cover + Get report CTA.
 */
export function ReportPromoModalV1({ onClose, copy }: ReportPromoModalV1Props) {
  const report = getReportById(copy.reportId)
  const { userMainSubscriptionId } = useChat()
  const { purchase, isReportPurchasing } = useReportPurchase({
    userMainSubscriptionId,
  })
  const { plans } = usePlans([copy.planId])

  const isLoading = report ? isReportPurchasing(report) : false
  const price = plans[0]?.price ?? DEFAULT_PRICE

  if (!report) return null

  const handleGetReport = async () => {
    const ok = await purchase(report, copy.planId)
    if (ok) onClose()
  }

  return (
    <div
      data-slot="report-promo-modal-v1"
      className="fixed inset-0 z-[1200] mx-auto flex min-h-[100dvh] w-full max-w-app flex-col justify-end bg-dark-base/80 backdrop-blur-[10px]"
    >
      <div className="flex w-full flex-col items-center rounded-t-3xl bg-green">
        <div className="flex w-full items-center justify-between px-4 py-2">
          <div className="flex items-center justify-center gap-1">
            <img src={LockIcon} alt="" className="size-6 shrink-0" />
            <p className="font-body text-sm font-semibold text-dark-base">
              Your exclusive personal report is ready
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            disabled={isLoading}
            className="flex size-6 shrink-0 items-center justify-center text-dark-base disabled:opacity-50"
          >
            <img src={CloseIcon} alt="" className="size-6" />
          </button>
        </div>

        <div className="relative flex w-full flex-col items-center gap-6 overflow-hidden rounded-t-3xl bg-dark-surface px-4 pb-4 pt-6">
          <img
            src={ModalBg}
            alt=""
            aria-hidden
            className="pointer-events-none absolute inset-0 size-full rounded-t-3xl object-cover"
          />

          <div className="relative z-[1] flex w-full flex-col items-center gap-2 text-center">
            <h2 className="w-full font-display text-2xl font-medium text-purple-light">
              {copy.title}
            </h2>
            <p className="w-full font-body text-base text-purple-light/70">
              {copy.description}
            </p>
          </div>

          <div className="relative z-[1] flex flex-col items-center">
            <div className="flex items-center justify-center rounded-t-md bg-purple-light/20 px-4 py-1 backdrop-blur-[5px]">
              <p className="font-body text-xs font-semibold text-white">
                ${price.toFixed(2)}
              </p>
            </div>
            <img
              src={report.hugeCover}
              alt=""
              className="h-[200px] w-[350px] object-contain drop-shadow-[0_30px_20px_rgba(13,8,28,0.8)]"
            />
          </div>

          <div className="relative z-[1] flex w-full flex-col items-center gap-2">
            <button
              type="button"
              onClick={() => void handleGetReport()}
              disabled={isLoading}
              className="flex h-14 w-full items-center justify-center gap-2 rounded-[80px] bg-gradient-to-b from-peach to-purple-accent font-body text-base font-semibold text-white disabled:bg-none disabled:bg-purple-light/10 disabled:text-purple-light/70"
            >
              {isLoading ? (
                <>
                  <span className="size-5 animate-spin rounded-full border-2 border-purple-light border-t-transparent" />
                  Purchasing...
                </>
              ) : (
                "Get report"
              )}
            </button>
            <p className="w-full text-center font-body text-sm text-purple-light/40">
              One-time payment is non-refundable
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
